import React from 'react';
import { Button, ButtonType, ButtonSize, IconName } from '@hills-design-system/components';
import { downloadFile } from '../../utils/downloadFile';
import { getAssetUrl } from '../../utils/assetUrl';

interface DigitalToolkitItem {
  label: string;
  body: string;
  image: { url: string; description?: string } | null;
  url: string;
  external?: boolean;
  _metadata?: { uid: string };
}

interface DigitalToolkitGroup {
  group_title: string;
  group_subtitle: string;
  group_icon: { url: string; description?: string } | null;
  items: DigitalToolkitItem[];
  _metadata?: { uid: string };
}

interface CmsDigitalToolkitData {
  name?: string;
  body?: string;
  component?: string;
  buttons?: {
    text: string;
    url: string;
    button_type: string | null;
    variant: string | null;
    inverted: boolean;
    icon: string;
    icon_position: string | null;
    external: boolean;
  };
  groups: DigitalToolkitGroup[];
}

interface CmsDigitalToolkitProps {
  data: CmsDigitalToolkitData;
}

// The label is a stable CMS identifier; the body contains the visible button text.
const isDownloadAllItem = (item: DigitalToolkitItem) => item.label === 'downloadAll';

const getButtonText = (body: string) => {
  const document = new DOMParser().parseFromString(body, 'text/html');
  return document.body.textContent?.trim() ?? '';
};

const CmsDigitalToolkit: React.FC<CmsDigitalToolkitProps> = ({ data }) => {
  const { body, buttons, groups } = data;

  return (
    <div className="flex flex-col items-start gap-[40px]">
      {/* Body + Download all row (from top-level buttons) */}
      {(body || buttons?.text) && (
        <div className="flex items-start justify-between self-stretch">
          {body && (
            <p className="text-body-100 leading-body font-medium text-gray-925 max-w-[500px]">
              {body}
            </p>
          )}
          {buttons?.text && (
            <div className="shrink-0">
              <Button
                label={buttons.text}
                type={ButtonType.Primary}
                size={ButtonSize.Medium}
                icon={buttons.button_type === 'Download' ? IconName.Download : undefined}
                onClick={() => {
                  if (buttons.url) {
                    window.open(buttons.url, buttons.external ? '_blank' : '_self');
                  }
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Groups */}
      <div className="flex flex-col items-start gap-[64px] self-stretch">
        {groups.map((group) => {
          const downloadAllItem = group.items.find(isDownloadAllItem);
          const regularItems = group.items.filter((item) => !isDownloadAllItem(item));

          return (
            <div
              key={group._metadata?.uid ?? group.group_title}
              className="flex flex-col gap-[40px] self-stretch"
            >
              {/* Section header */}
              <div className="flex items-start justify-between self-stretch">
                <div className="flex flex-col gap-1">
                  <h2 className="text-heading-200 font-bold text-gray-925">
                    {group.group_title}
                  </h2>
                  {group.group_subtitle && (
                    <p
                      className="text-body-100 leading-body font-medium text-gray-925 max-w-[500px] [&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer"
                      dangerouslySetInnerHTML={{ __html: group.group_subtitle }}
                    />
                  )}
                </div>
                {downloadAllItem && (
                  <div className="shrink-0">
                    <Button
                      label={getButtonText(downloadAllItem.body)}
                      type={ButtonType.Primary}
                      size={ButtonSize.Medium}
                      icon={IconName.Download}
                      onClick={() => {
                        const fileUrl = downloadAllItem.url || downloadAllItem.image?.url;
                        if (fileUrl) {
                          downloadFile(fileUrl);
                        }
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Items grid */}
              <div className="grid grid-cols-4 gap-6 items-start">
                {regularItems.map((item) => (
                  <div
                    key={item._metadata?.uid ?? item.label}
                    className="flex flex-col gap-[8px] min-w-0"
                  >
                    {/* Thumbnail */}
                    <div className="w-full aspect-square bg-[#F8F8F8] rounded-lg flex items-center justify-center relative px-[48px] py-[61px]">
                      {item.image ? (
                        <img
                          src={item.image.url}
                          alt={item.image.description ?? item.label}
                          className="w-[180px] h-[180px] object-contain"
                        />
                      ) : (
                        <div className="w-[180px] h-[180px] bg-gray-100 rounded-lg" />
                      )}
                      <img
                        src={getAssetUrl('images/FPO.png')}
                        alt=""
                        className="absolute right-0 top-[8%] w-[156px] rotate-[10deg] opacity-75 select-none pointer-events-none"
                      />
                    </div>

                    {/* Label */}
                    <span className="text-detail-100 font-bold text-gray-925 leading-[137.5%]">
                      {item.label}
                    </span>

                    {/* Download links */}
                    {item.body && (
                      <div
                        className="text-body-100 font-medium leading-[162.5%] text-gray-925 [&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer [&_p]:whitespace-nowrap"
                        dangerouslySetInnerHTML={{ __html: item.body }}
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          const anchor = target.closest('a');
                          if (anchor?.href) {
                            e.preventDefault();
                            downloadFile(anchor.href);
                          }
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CmsDigitalToolkit;
